import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { RolesService, type UpdateRoleDto, type Module, type Role } from '../../services/security/roles.service';
import { NotificationToast } from '../ui/NotificationToast';
import { Settings, Search, Check, LayoutGrid, Package, ShoppingCart, Users, BarChart3, Shield, CreditCard, Truck, MessageSquare, Pill, Tag, FileText, Bell, Calendar, Bookmark, HelpCircle } from 'lucide-react';

const updateRoleSchema = z.object({
  nombre: z.string().min(1, 'Nombre es requerido').min(3, 'Mínimo 3 caracteres'),
  descripcion: z.string().optional(),
  moduloIds: z.array(z.string()).min(1, 'Debe seleccionar al menos un módulo')
});

type UpdateRoleFormData = z.infer<typeof updateRoleSchema>;

interface EditRoleDrawerProps {
  isOpen: boolean;
  role: Role | null;
  onClose: () => void;
  onSuccess?: () => void;
  onUpdateResult?: (success: boolean, message: string) => void;
}

export const EditRoleDrawer: React.FC<EditRoleDrawerProps> = ({
  isOpen,
  role,
  onClose,
  onSuccess,
  onUpdateResult
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [updatedRole, setUpdatedRole] = useState<any>(null);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Estados para módulos
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<UpdateRoleFormData>({
    resolver: zodResolver(updateRoleSchema),
    defaultValues: {
      moduloIds: []
    }
  });

  useEffect(() => {
    if (isOpen && role) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      loadModules();
      loadRoleData();
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setUpdatedRole(null);
        setSelectedModules([]);
        setModuleSearchTerm('');
        reset();
      }, 300);
    }
  }, [isOpen, role, reset]);

  const loadRoleData = () => {
    if (role) {
      // Cargar datos del rol en el formulario
      setValue('nombre', role.nombre);
      setValue('descripcion', role.descripcion || '');
      
      // Cargar módulos seleccionados
      const roleModuleIds = role.modulos?.map(m => m.id) || [];
      setSelectedModules(roleModuleIds);
      setValue('moduloIds', roleModuleIds);
    }
  };

  const loadModules = async () => {
    setIsLoadingModules(true);
    try {
      const modulesData = await RolesService.getAvailableModules();
      
      // Verificar que modulesData sea un array antes de usar filter
      const moduleArray = Array.isArray(modulesData) ? modulesData : [];
      
      const activeModules = moduleArray.filter(module => module.activo);
      
      setModules(activeModules); // Solo módulos activos
    } catch (error) {
      console.error('Error al cargar módulos:', error);
      setErrorMessage('Error al cargar los módulos disponibles');
    } finally {
      setIsLoadingModules(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const onSubmit = async (data: UpdateRoleFormData) => {
    if (!role) return;
    
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const roleData: UpdateRoleDto = {
        nombre: data.nombre,
        descripcion: data.descripcion,
        moduloIds: selectedModules
      };

      const result = await RolesService.updateRole(role.id, roleData);
      
      setUpdatedRole({
        id: result.id,
        nombre: result.nombre,
        descripcion: result.descripcion,
        modulosAsignados: selectedModules.length
      });

      const message = `El rol ${result.nombre} fue actualizado exitosamente.`;
      
      setNotificationConfig({
        title: '¡Rol actualizado!',
        message,
        type: 'success'
      });
      setShowNotification(true);

      if (onUpdateResult) {
        onUpdateResult(true, message);
      }

      setTimeout(() => {
        handleClose();
      }, 2000);
      
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error al actualizar rol:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al actualizar rol';
      setErrorMessage(errorMsg);
      
      setNotificationConfig({
        title: 'Error al actualizar rol',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
      
      if (onUpdateResult) {
        onUpdateResult(false, errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    handleClose();
  };


  const toggleModule = (moduleId: string) => {
    const newSelected = selectedModules.includes(moduleId)
      ? selectedModules.filter(id => id !== moduleId)
      : [...selectedModules, moduleId];
    
    setSelectedModules(newSelected);
    setValue('moduloIds', newSelected);
  };

  const filteredModules = modules.filter(module =>
    module.nombre.toLowerCase().includes(moduleSearchTerm.toLowerCase()) ||
    (module.descripcion && module.descripcion.toLowerCase().includes(moduleSearchTerm.toLowerCase()))
  );

  if (!isOpen || !role) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        <section className={`absolute right-0 top-0 h-full w-full max-w-6xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-8 py-5 bg-gradient-to-r from-purple-600 to-indigo-600 border-b border-purple-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Editar Rol
                    </h2>
                    <p className="text-sm text-purple-100 mt-0.5">
                      {role.nombre}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 text-sm">{errorMessage}</p>
                  </div>
                </div>
              )}

              {!updatedRole ? (
                <>
                  {/* Instrucciones */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Modificar información del rol
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Modifica los campos que deseas actualizar</li>
                        <li>• El nombre del rol debe ser único en el sistema</li>
                        <li>• Actualiza los módulos asignados según sea necesario</li>
                        <li>• Los cambios se aplicarán inmediatamente</li>
                      </ul>
                    </div>
                  </div>

                  {/* Formulario */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    {/* Layout de 2 columnas */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Información básica - columna izquierda */}
                      <div className="lg:col-span-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Settings className="w-5 h-5 text-purple-500" />
                          Información Básica
                        </h3>
                        
                        <div className="space-y-5 bg-gray-50 rounded-xl p-5 border border-gray-100">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Nombre del Rol *
                            </label>
                            <input
                              type="text"
                              {...register('nombre')}
                              placeholder="Ej: Administrador, Editor, Supervisor"
                              className={`w-full px-4 py-3 border rounded-lg transition-all text-sm ${
                                errors.nombre
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.nombre && (
                              <p className="mt-1.5 text-sm text-red-500">{errors.nombre.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Descripción
                            </label>
                            <textarea
                              {...register('descripcion')}
                              placeholder="Describe las responsabilidades y alcance de este rol"
                              rows={4}
                              className={`w-full px-4 py-3 border rounded-lg transition-all text-sm ${
                                errors.descripcion
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none resize-none`}
                            />
                            {errors.descripcion && (
                              <p className="mt-1.5 text-sm text-red-500">{errors.descripcion.message}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Selección de Módulos - columna derecha */}
                      <div className="lg:col-span-2">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <LayoutGrid className="w-5 h-5 text-purple-500" />
                            Módulos del Sistema
                          </h3>
                          <span className="text-sm font-medium text-purple-700 bg-purple-50 px-3 py-1 rounded-full">
                            {selectedModules.length} asignado{selectedModules.length !== 1 ? 's' : ''}
                          </span>
                        </div>

                      {isLoadingModules ? (
                        <div className="flex items-center justify-center py-12 border border-gray-200 rounded-xl bg-gray-50">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                          <span className="ml-2 text-sm text-gray-500">Cargando módulos...</span>
                        </div>
                      ) : (
                        <>
                          {/* Buscador */}
                          <div className="relative mb-5">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="text"
                              placeholder="Buscar módulos..."
                              value={moduleSearchTerm}
                              onChange={(e) => setModuleSearchTerm(e.target.value)}
                              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none text-sm"
                            />
                            {moduleSearchTerm && (
                              <button
                                type="button"
                                onClick={() => setModuleSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                ✕
                              </button>
                            )}
                          </div>

                          {/* Módulos Asignados */}
                          {selectedModules.length > 0 && !moduleSearchTerm && (
                            <div className="mb-6">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                Asignados a este rol
                              </p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {modules
                                  .filter(m => selectedModules.includes(m.id))
                                  .map(module => (
                                    <button
                                      key={module.id}
                                      type="button"
                                      onClick={() => toggleModule(module.id)}
                                      className="relative flex items-start gap-3 p-4 rounded-xl border-2 border-purple-500 bg-purple-50 text-left transition-all hover:bg-purple-100 hover:shadow-md group"
                                      title="Quitar módulo"
                                    >
                                      <div className="mt-0.5 w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0">
                                        <Check className="w-3 h-3" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-purple-900">{module.nombre}</p>
                                        {module.descripcion && (
                                          <p className="text-xs text-purple-700 mt-0.5 truncate">{module.descripcion}</p>
                                        )}
                                      </div>
                                      <span className="absolute top-2 right-2 text-[10px] text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                        Quitar
                                      </span>
                                    </button>
                                  ))}
                              </div>
                            </div>
                          )}

                          {/* Módulos Disponibles */}
                          <div>
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                              {moduleSearchTerm ? 'Resultados de búsqueda' : 'Disponibles'}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {filteredModules
                                .filter(m => !selectedModules.includes(m.id))
                                .map(module => (
                                  <button
                                    key={module.id}
                                    type="button"
                                    onClick={() => toggleModule(module.id)}
                                    className="relative flex items-start gap-3 p-4 rounded-xl border border-gray-200 bg-white text-left transition-all hover:border-purple-300 hover:shadow-sm group"
                                    title="Agregar módulo"
                                  >
                                    <div className="mt-0.5 w-5 h-5 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0 group-hover:border-purple-400 transition-colors">
                                      <Check className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900">{module.nombre}</p>
                                      {module.descripcion && (
                                        <p className="text-xs text-gray-500 mt-0.5 truncate">{module.descripcion}</p>
                                      )}
                                    </div>
                                    <span className="absolute top-2 right-2 text-[10px] text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                      Agregar
                                    </span>
                                  </button>
                                ))}
                            </div>

                            {/* Cuando el buscador filtra módulos ya asignados */}
                            {moduleSearchTerm && filteredModules.some(m => selectedModules.includes(m.id)) && (
                              <div className="mt-5">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                  Ya asignados
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {filteredModules
                                    .filter(m => selectedModules.includes(m.id))
                                    .map(module => (
                                      <button
                                        key={module.id}
                                        type="button"
                                        onClick={() => toggleModule(module.id)}
                                        className="relative flex items-start gap-3 p-4 rounded-xl border-2 border-purple-500 bg-purple-50 text-left transition-all hover:bg-purple-100 hover:shadow-md group"
                                        title="Quitar módulo"
                                      >
                                        <div className="mt-0.5 w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center flex-shrink-0">
                                          <Check className="w-3 h-3" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-semibold text-purple-900">{module.nombre}</p>
                                          {module.descripcion && (
                                            <p className="text-xs text-purple-700 mt-0.5 truncate">{module.descripcion}</p>
                                          )}
                                        </div>
                                        <span className="absolute top-2 right-2 text-[10px] text-purple-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                          Quitar
                                        </span>
                                      </button>
                                    ))}
                                </div>
                              </div>
                            )}

                            {filteredModules.length === 0 && (
                              <div className="text-center py-10 text-gray-500 border border-dashed border-gray-200 rounded-xl">
                                <Settings className="mx-auto h-10 w-10 text-gray-300 mb-2" />
                                <p className="text-sm">
                                  {moduleSearchTerm ? 'No se encontraron módulos' : 'No hay módulos disponibles'}
                                </p>
                              </div>
                            )}
                          </div>

                          {errors.moduloIds && (
                            <p className="mt-3 text-sm text-red-500">{errors.moduloIds.message}</p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  </form>
                </>
              ) : (
                /* Resultados de la actualización */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Rol actualizado exitosamente
                    </h3>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Información actualizada</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Nombre:</span>
                        <span className="text-sm font-medium text-gray-900">{updatedRole.nombre}</span>
                      </div>
                      {updatedRole.descripcion && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Descripción:</span>
                          <span className="text-sm font-medium text-gray-900">{updatedRole.descripcion}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Módulos asignados:</span>
                        <span className="text-sm font-medium text-gray-900">{updatedRole.modulosAsignados}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">ID:</span>
                        <span className="text-sm font-mono text-gray-900">{updatedRole.id}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-blue-400 mr-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm text-blue-800 font-medium">Cambios aplicados</p>
                        <p className="text-sm text-blue-700 mt-1">Las modificaciones han sido guardadas y están disponibles inmediatamente en el sistema.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 py-5 bg-gray-50 border-t border-gray-200">
              {!updatedRole ? (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading}
                    className="px-8 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm shadow-md"
                  >
                    {isLoading ? 'Actualizando...' : 'Guardar cambios'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleContinue}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:opacity-90 transition-all font-medium shadow-md"
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default EditRoleDrawer;